import {
  type AdminProductListItem,
  type BannerResponse,
  type CreateBannerDto,
  type UpdateBannerDto,
} from '@aps/contracts';
import { formatMoney } from '@aps/ui';
import {
  EMPTY_LINK_DRAFT,
  linkDraftOf,
  linkUrlOf,
  type BannerLinkDraft,
} from './BannerLinkField.js';

/** Banner formasi holati: hamma maydon matn — `<input>` qiymati shunday. */
export interface BannerDraft extends BannerLinkDraft {
  titleUz: string;
  titleRu: string;
  subtitleUz: string;
  subtitleRu: string;
  badgeUz: string;
  badgeRu: string;
  colorKey: string;
  startsAt: string;
  endsAt: string;
  sort: string;
}

export const EMPTY_BANNER_DRAFT: BannerDraft = {
  titleUz: '',
  titleRu: '',
  subtitleUz: '',
  subtitleRu: '',
  badgeUz: '',
  badgeRu: '',
  colorKey: '',
  startsAt: '',
  endsAt: '',
  sort: '',
  ...EMPTY_LINK_DRAFT,
};

/** ISO vaqt → `<input type="datetime-local">` qiymati (mahalliy vaqtda). */
export function localDateTime(value: string | null): string {
  if (value === null) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function bannerDraft(row: BannerResponse): BannerDraft {
  return {
    ...linkDraftOf(row.linkUrl),
    titleUz: row.titleUz,
    titleRu: row.titleRu ?? '',
    subtitleUz: row.subtitleUz ?? '',
    subtitleRu: row.subtitleRu ?? '',
    badgeUz: row.badgeUz ?? '',
    badgeRu: row.badgeRu ?? '',
    colorKey: row.colorKey ?? '',
    startsAt: localDateTime(row.startsAt),
    endsAt: localDateTime(row.endsAt),
    sort: String(row.sort),
  };
}

/**
 * Tovardan banner matni: sarlavha — nom, yorliq — "brend · artikul", belgi — narx.
 * Narxi yo'q ("Tez kunda") tovarda belgi bo'sh qoladi: narx ko'rsatilmaydi.
 * Maydon uzunligi serverdagi chegaradan oshmaydi (sarlavha 120, yorliq 200, belgi 40).
 */
export function bannerTextFromProduct(
  product: AdminProductListItem,
): Pick<BannerDraft, 'titleUz' | 'titleRu' | 'subtitleUz' | 'subtitleRu' | 'badgeUz' | 'badgeRu'> {
  const subtitle = `${product.brandName} · ${product.sku}`.slice(0, 200);
  return {
    titleUz: product.nameUz.slice(0, 120),
    titleRu: (product.nameRu ?? '').slice(0, 120),
    subtitleUz: subtitle,
    subtitleRu: subtitle,
    badgeUz: product.comingSoon ? '' : formatMoney(product.price, 'UZ').slice(0, 40),
    badgeRu: product.comingSoon ? '' : formatMoney(product.price, 'RU').slice(0, 40),
  };
}

function parsed(value: string): Date | null {
  return value === '' ? null : new Date(value);
}

/** Saqlashdan oldingi tekshiruv: har biri o'z maydoni yonida ko'rsatiladi. */
export function bannerProblems(
  draft: BannerDraft,
  isNew: boolean,
): { title: boolean; link: boolean; dates: boolean; sort: boolean } {
  const startsAt = parsed(draft.startsAt);
  const endsAt = parsed(draft.endsAt);
  return {
    title: draft.titleUz.trim() === '',
    link: linkUrlOf(draft).incomplete,
    dates: !(
      (startsAt === null || !Number.isNaN(startsAt.getTime())) &&
      (endsAt === null || !Number.isNaN(endsAt.getTime())) &&
      (startsAt === null || endsAt === null || startsAt < endsAt)
    ),
    sort: !((draft.sort === '' && isNew) || /^(?:0|[1-9]\d{0,3})$/.test(draft.sort)),
  };
}

export function bannerCreateDto(draft: BannerDraft): CreateBannerDto {
  const optional = (value: string): string | undefined => value.trim() || undefined;
  const link = linkUrlOf(draft);
  const startsAt = parsed(draft.startsAt);
  const endsAt = parsed(draft.endsAt);
  return {
    titleUz: draft.titleUz.trim(),
    ...(optional(draft.titleRu) === undefined ? {} : { titleRu: draft.titleRu.trim() }),
    ...(optional(draft.subtitleUz) === undefined ? {} : { subtitleUz: draft.subtitleUz.trim() }),
    ...(optional(draft.subtitleRu) === undefined ? {} : { subtitleRu: draft.subtitleRu.trim() }),
    ...(optional(draft.badgeUz) === undefined ? {} : { badgeUz: draft.badgeUz.trim() }),
    ...(optional(draft.badgeRu) === undefined ? {} : { badgeRu: draft.badgeRu.trim() }),
    ...(draft.colorKey === '' ? {} : { colorKey: draft.colorKey }),
    ...(link.url === null ? {} : { linkUrl: link.url }),
    ...(startsAt === null ? {} : { startsAt: startsAt.toISOString() }),
    ...(endsAt === null ? {} : { endsAt: endsAt.toISOString() }),
    ...(draft.sort === '' ? {} : { sort: Number(draft.sort) }),
  };
}

/** Faqat o'zgargan maydonlar (PATCH): tegilmagan sana qayta yozilmaydi. */
export function bannerUpdateDto(draft: BannerDraft, row: BannerResponse): UpdateBannerDto {
  const optionalValue = (value: string): string | null => value.trim() || null;
  const isoOrNull = (value: string): string | null => {
    const date = parsed(value);
    return date === null || Number.isNaN(date.getTime()) ? null : date.toISOString();
  };
  const link = linkUrlOf(draft);
  return {
    ...(draft.titleUz.trim() === row.titleUz ? {} : { titleUz: draft.titleUz.trim() }),
    ...(optionalValue(draft.titleRu) === row.titleRu
      ? {}
      : { titleRu: optionalValue(draft.titleRu) }),
    ...(optionalValue(draft.subtitleUz) === row.subtitleUz
      ? {}
      : { subtitleUz: optionalValue(draft.subtitleUz) }),
    ...(optionalValue(draft.subtitleRu) === row.subtitleRu
      ? {}
      : { subtitleRu: optionalValue(draft.subtitleRu) }),
    ...(optionalValue(draft.badgeUz) === row.badgeUz
      ? {}
      : { badgeUz: optionalValue(draft.badgeUz) }),
    ...(optionalValue(draft.badgeRu) === row.badgeRu
      ? {}
      : { badgeRu: optionalValue(draft.badgeRu) }),
    ...(optionalValue(draft.colorKey) === row.colorKey
      ? {}
      : { colorKey: optionalValue(draft.colorKey) }),
    ...(link.url === (linkDraftOf(row.linkUrl).linkType === 'none' ? null : row.linkUrl)
      ? {}
      : { linkUrl: link.url }),
    ...(draft.startsAt === localDateTime(row.startsAt)
      ? {}
      : { startsAt: isoOrNull(draft.startsAt) }),
    ...(draft.endsAt === localDateTime(row.endsAt) ? {} : { endsAt: isoOrNull(draft.endsAt) }),
    ...(Number(draft.sort) === row.sort ? {} : { sort: Number(draft.sort) }),
  };
}

/**
 * Banner mijozga hozir ko'rinadimi? Haqiqiy oyna SERVERDA tekshiriladi; bu yerda faqat admin
 * "nega ko'rinmayapti" degan savolga javob olishi uchun ko'rsatiladi.
 */
export type BannerState = 'live' | 'hidden' | 'scheduled' | 'expired';

export function bannerState(row: BannerResponse, now: Date): BannerState {
  if (!row.isActive) return 'hidden';
  if (row.endsAt !== null && new Date(row.endsAt) < now) return 'expired';
  if (row.startsAt !== null && new Date(row.startsAt) > now) return 'scheduled';
  return 'live';
}
